import { passedThreshold, type Point } from '../../shared/lib/pointer-drag';

/**
 * Drawer swipe maths (Phase 171b) — the DOM-free half of `useDrawerSwipe`.
 *
 * Two gestures share one panel: an OPEN starts at the left screen edge with
 * the panel off-screen (offset -width) and drags right; a CLOSE starts on the
 * open panel (offset 0) and drags left. Both commit on distance or velocity.
 */

/** Width of the invisible strip along the left edge that starts an open. */
export const EDGE_ZONE_PX = 24;
/** Movement before a press becomes a swipe — same as the drag threshold. */
export const ARM_PX = 8;
/** Commit once the panel has travelled this share of its own width… */
export const COMMIT_FRACTION = 0.35;
/** …or is moving this fast in the gesture's direction (px/ms = 500 px/s). */
export const COMMIT_VELOCITY = 0.5;

export type DrawerGesture = 'open' | 'close';

export interface Sample {
  x: number;
  t: number;
}

/** Moved at least ARM_PX, and more sideways than up/down. */
export function isHorizontalIntent(from: Point, to: Point): boolean {
  if (!passedThreshold(from, to, ARM_PX)) return false;
  return Math.abs(to.x - from.x) >= Math.abs(to.y - from.y);
}

/** Panel offset in px for a finger displacement `dx`, clamped to [-width, 0]. */
export function panelOffset(kind: DrawerGesture, dx: number, width: number): number {
  const base = kind === 'open' ? -width : 0;
  return Math.min(0, Math.max(-width, base + dx));
}

/** Commit when far enough OR fast enough in the gesture's direction; never against it. */
export function shouldCommit(
  kind: DrawerGesture,
  dx: number,
  velocity: number,
  width: number,
): boolean {
  const sign = kind === 'open' ? 1 : -1;
  const travelled = dx * sign;
  if (travelled <= 0) return false;
  return travelled >= width * COMMIT_FRACTION || velocity * sign >= COMMIT_VELOCITY;
}

/** px/ms between two samples; 0 when no time has passed. */
export function velocityOf(prev: Sample, next: Sample): number {
  const dt = next.t - prev.t;
  return dt > 0 ? (next.x - prev.x) / dt : 0;
}
